<template>
  <div>
    <div :id="chartId" class="container-chart">
      <h6 class="title-font mt-2" style="text-align: center">{{ config.titles[$locale] }}</h6>
      <div class="lds-ripple" v-if="isDataLoading">
        <div></div>
        <div></div>
      </div>
      <chart-error :dataLoadedError="dataLoadedError" @reload="reloadData"></chart-error>
    </div>
  </div>
</template>

<script>
import * as d3 from 'd3'
import d3Tip from 'd3-tip'
import { BarChartView } from '../../utils/charts/BarChartView'
import { utilsMixin, chartMixin } from '../../utils/mixins'
import { formatIndicatorToDistributionDataset } from '../../utils/chartDataFormatter'

export default {
  name: 'BarChart',
  dependencies: ['dataService'],
  mixins: [utilsMixin, chartMixin],
  data() {
    return {
      dataset: {},
      sortedDataset: {},
      checked: [],
      chart: null,
      savedStart: null,
      savedEnd: null,
      chartWidth: 960,
      chartHeight: 400,
    }
  },
  props: ['config', 'chartId', 'period', 'mode', 'previewDataset'],
  mounted() {
    d3.tip = d3Tip

    this.initChartView()

    if (this.mode != 'preview') {
      this.savedStart = this.period[0]
      this.savedEnd = this.period[1]
    } else {
      setTimeout(() => {
        this.dataset = this.previewDataset
        this.processData()
        this.displayChart()
      }, 200)
    }
  },
  beforeDestroy() {
    this.dataService.cancelDataRequest(this.currentRequestId)
  },
  components: {
    'chart-error': () => import('../utils/ChartErrorReload'),
  },
  watch: {
    config: {
      handler: function() {
        if (this.hasToRequestNewData() && this.mode !== 'preview') {
          this.loadConfigAndFetchData()
        } else {
          this.updateChartData()
          this.displayChart()
        }
        this.previousConfig = JSON.parse(JSON.stringify(this.config))
      },
      immediate: true,
    },
    period: {
      handler: function(period) {
        if (
          (period[0] != this.savedStart || period[1] != this.savedEnd) &&
          this.mode != 'preview'
        ) {
          this.savedStart = this.period[0]
          this.savedEnd = this.period[1]
          this.loadConfigAndFetchData()
        }
      },
      immediate: false,
    },
  },
  methods: {
    initChartView() {
      //Create chart view
      this.chart = new BarChartView(
        this.chartId,
        this.sortedDataset,
        this.config.type,
        {
          width: this.chartWidth,
          height: this.chartHeight,
        },
        {
          top: 5,
          right: 20,
          bottom: 30,
          left: 50,
        },
      )

      this.chart.initContainer(this.chartId)
      this.chart.setMode(this.mode)
    },
    loadConfigAndFetchData: function() {
      this.requestCount++

      this.dataService
        .getChartData(
          this.config,
          this.period,
          this.dateFormat,
          this.getCurrentUser,
          this.currentRequestId,
        )
        .then(data => {
          this.chart.setTitles(this.config.titles[this.$locale])
          this.$emit('update', {
            chart_id: this.config.id,
            data: JSON.parse(JSON.stringify(data.result)),
          })

          this.dataset = data.result
          this.processData()
          this.displayChart()
          this.requestCount--
          this.dataLoadedError = false
        })
        .catch(e => {
          if (e.message != 'cancel') {
            console.error('BarChart error', e)
            this.dataLoadedError = true
          }
          this.requestCount--
        })

      this.currentRequestId = this.getRequestId(
        this.config,
        this.period,
        this.dateFormat,
        this.getCurrentUser,
      )
    },
    processData: function() {
      this.dataset =
        this.config.distribution_type && this.config.distribution_type !== 'all'
          ? this.dataset
          : formatIndicatorToDistributionDataset(this.dataset) // Process returned data from indicator if needed

      if (this.dataset.distributionType !== 'duration') {
        // Use to calculate an "others" category for small value results < 3%
        let foundSmallValues = this.dataset.items.filter(item => {
          return item.percent < 3
        })

        if (foundSmallValues && foundSmallValues.length > 1) {
          this.otherCategories = foundSmallValues

          this.dataset.items.push({
            category: 'others',
            count: this.otherCategories.reduce(
              (accumulator, value) => accumulator + Number(value.count),
              0,
            ),
            percent: this.otherCategories.reduce(
              (accumulator, value) => accumulator + Number(value.percent),
              0,
            ),
          })

          this.dataset.items = this.dataset.items.filter(item => {
            return item.percent >= 3 || item.category === 'others'
          })
        }

        this.dataset.items.sort((a, b) => {
          return String(a.category) - String(b.category)
        })

        if (this.otherCategories && this.otherCategories.length > 1) {
          this.otherCategories.sort((a, b) => {
            return b.count - a.count
          })
        }
      }

      //We create a copy of the dataset to allow legends filter on user's click
      this.sortedDataset = JSON.parse(JSON.stringify(this.dataset))
      this.checked = this.dataset.items.map(item => {
        return item.category
      })

      this.updateChartData()
    },
    updateChartData: function() {
      this.sortData()

      //If chart already displayed
      if (
        this.isSameConfig() &&
        this.chart.sortedDataset &&
        this.chart.sortedDataset.items &&
        this.chart.checked &&
        this.chart.checked.length > 0
      ) {
        //update checked legends according if new categories appears and previous unchecked legends
        this.updateCheckedLegends()

        this.sortedDataset.items = this.dataset.items.filter(item => {
          return this.chart.checked.indexOf(item.category) > -1
        })
      } else {
        //Set a copy of checked legends
        this.chart.setChecked(JSON.parse(JSON.stringify(this.checked)))
        this.chart.setUnchecked([])
      }

      this.chart.setTitles(
        this.config.titles[this.$locale],
        '',
        this.dataset.unit !== 'no' ? `(${this.getUnitLabel(this.dataset)})` : '',
      )
      this.chart.setDataset(this.dataset)
      this.chart.setSortedDataset(this.sortedDataset)
      this.chart.setConfig(this.config)
    },
    displayChart: function() {
      d3.select('#' + this.chartId + ' svg').remove()
      this.chart.initContainer(this.chartId)
      this.chart.initTooltip(this.otherCategories)
      this.chart.draw()
      this.chart.displayGrid()
    },
    sortData: function() {
      if (this.config.display_options && this.config.display_options.display_by) {
        let displayBy = this.config.display_options.display_by

        switch (displayBy) {
          case 'value_asc':
            if (this.dataset.dataValue !== 'min_level_gradient') {
              this.dataset.items.sort((a, b) => {
                return a.count - b.count
              })
            } else {
              this.dataset.items.sort((a, b) => {
                return b.count - a.count
              })
            }
            break
          case 'value_desc':
            if (this.dataset.dataValue !== 'min_level_gradient') {
              this.dataset.items.sort((a, b) => {
                return b.count - a.count
              })
            } else {
              this.dataset.items.sort((a, b) => {
                return a.count - b.count
              })
            }

            break
          case 'site_id_asc':
            this.dataset.items.sort((a, b) => {
              return Number(a.category) - Number(b.category)
            })
            break
          case 'site_id_desc':
            this.dataset.items.sort((a, b) => {
              return Number(b.category) - Number(a.category)
            })
            break
          case 'alpha_asc':
            this.dataset.items.sort((a, b) => {
              let aKeyValue = this.$t(`${this.config.sort_by}.${a.category}`)
              let bKeyValue = this.$t(`${this.config.sort_by}.${b.category}`)
              if (aKeyValue > bKeyValue) return 1
              if (aKeyValue < bKeyValue) return -1
              else return 0
            })
            break
          case 'alpha_desc':
            this.dataset.items.sort((a, b) => {
              let aKeyValue = this.$t(`${this.config.sort_by}.${a.category}`)
              let bKeyValue = this.$t(`${this.config.sort_by}.${b.category}`)
              if (aKeyValue > bKeyValue) return -1
              if (aKeyValue < bKeyValue) return 1
              else return 0
            })
            break
          default:
            this.dataset.items.sort((a, b) => {
              return b.count - a.count
            })
            break
        }
      }
      this.sortedDataset.items = this.dataset.items
    },
  },
}
</script>
